/**
 * Auto-advance countdown for Present Mode's playlist indicator (UX review: the
 * "1 / 2" indicator was static, giving no hint that — or when — the dashboard
 * would switch). Fed by the SAME 1s tick that advances the playlist
 * (`usePresentPlaylist` publishes the cycle into `presentRequestStore`), so the
 * ring can never disagree with the switch it predicts, and no second timer
 * exists. Renders nothing unless the playlist is actually auto-advancing.
 *
 * Mount it with `key={index}` — remounting on each advance keeps the CSS
 * transition from animating the ring backwards on the wrap to 0.
 */
import { playlistCycleProgress, playlistSecondsRemaining } from '../services/playlistSequence';
import { usePresentRequestStore } from '../store/presentRequestStore';

const RADIUS = 5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function PlaylistCountdownRing() {
  const cycle = usePresentRequestStore((s) => s.cycle);
  const progress = playlistCycleProgress(cycle.elapsedMs, cycle.cycleMs);
  const remaining = playlistSecondsRemaining(cycle.elapsedMs, cycle.cycleMs);
  if (progress === null || remaining === null) return null;
  return (
    <span
      className="flex items-center"
      title={`Next dashboard in ${remaining}s`}
      data-testid="present-mode-countdown"
      data-progress={progress.toFixed(2)}
    >
      <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 -rotate-90" aria-hidden>
        <circle cx="7" cy="7" r={RADIUS} fill="none" strokeWidth="2" className="stroke-muted-foreground/25" />
        <circle
          cx="7"
          cy="7"
          r={RADIUS}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
          className="stroke-foreground/70 transition-[stroke-dashoffset] duration-1000 ease-linear"
        />
      </svg>
      <span className="sr-only">Next dashboard in {remaining} seconds</span>
    </span>
  );
}
